import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Card,
  Statistic,
  Tag,
  Typography,
  Row,
  Col,
  Button,
  Input,
  Select,
  Space,
  Empty,
  message,
  Spin,
} from "antd";
import * as XLSX from "xlsx";
import { AppContext } from "../context/AppContext";
import axios from "axios";
import {
  SearchOutlined,
  DownloadOutlined,
  PlusOutlined,
  DollarOutlined,
  FundOutlined,
  RiseOutlined,
  BankOutlined,
} from "@ant-design/icons";
import moment from "moment";

const { Title, Text } = Typography;

const zambianColors = {
  green: "#4d8c4a",
  red: "#d16b6b",
  orange: "#e99e54",
  black: "#444444",
  lightGreen: "#80b782",
  lightOrange: "#f4c28e",
};

const DIRECTUS_URL = "https://zedlenders.pickmesms.com";
const JUTEM_COMPANY_ID = 22;

const JutemExpensesPage = () => {
  const { company } = useContext(AppContext);
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [selectedYear, setSelectedYear] = useState(String(new Date().getFullYear()));
  const [selectedMonth, setSelectedMonth] = useState(moment().format("YYYY-MM"));
  const [selectedCategory, setSelectedCategory] = useState(null);

  const token = localStorage.getItem("token");

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${DIRECTUS_URL}/items/transactions`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          "filter[company][_eq]": JUTEM_COMPANY_ID,
          "filter[transaction_type][_eq]": "Operational Cost",
          fields: "*",
          limit: -1,
          sort: "-transaction_date",
        },
      });
      setExpenses(res.data.data || []);
    } catch (err) {
      if (err.response?.status === 403 || err.response?.status === 404) {
        setExpenses([]);
      } else {
        message.error("Failed to load expenses");
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchExpenses();
    }
  }, [token]);

  const getMonthKey = (row) => {
    if (!row.transaction_date) return null;
    return row.transaction_date.substring(0, 7);
  };

  const availableYears = useMemo(() => {
    const years = [
      ...new Set(
        expenses
          .map((e) => e.transaction_date?.substring(0, 4))
          .filter(Boolean)
      ),
    ]
      .sort()
      .reverse();
    return years;
  }, [expenses]);

  const availableMonths = useMemo(() => {
    let source = expenses;
    if (selectedYear) {
      source = source.filter((e) => e.transaction_date?.startsWith(selectedYear));
    }
    const months = [
      ...new Set(source.map((e) => getMonthKey(e)).filter(Boolean)),
    ]
      .sort()
      .reverse();
    return months;
  }, [expenses, selectedYear]);

  const availableCategories = useMemo(() => {
    const cats = [
      ...new Set(
        expenses.map((e) => (e.notes || "").trim()).filter(Boolean)
      ),
    ].sort();
    return cats;
  }, [expenses]);

  const filteredData = useMemo(() => {
    let filtered = [...expenses];

    if (searchText) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter((e) =>
        (e.notes || "").toLowerCase().includes(search)
      );
    }
    if (selectedYear) {
      filtered = filtered.filter((e) =>
        e.transaction_date?.startsWith(selectedYear)
      );
    }
    if (selectedMonth) {
      filtered = filtered.filter((e) => getMonthKey(e) === selectedMonth);
    }
    if (selectedCategory) {
      filtered = filtered.filter(
        (e) => (e.notes || "").trim() === selectedCategory
      );
    }

    return filtered;
  }, [expenses, searchText, selectedYear, selectedMonth, selectedCategory]);

  const stats = useMemo(() => {
    const amounts = filteredData.map((e) => parseFloat(e.amount || 0));
    const total = amounts.reduce((s, a) => s + a, 0);
    const highest = amounts.length > 0 ? Math.max(...amounts) : 0;
    const average = amounts.length > 0 ? total / amounts.length : 0;
    return {
      totalExpenses: total,
      transactionCount: filteredData.length,
      highestExpense: highest,
      averageExpense: average,
    };
  }, [filteredData]);

  const handleExport = () => {
    if (filteredData.length === 0) {
      message.warning("No data to export");
      return;
    }

    const exportData = filteredData.map((r, i) => ({
      "#": i + 1,
      Description: r.notes || "",
      "Amount (ZMW)": parseFloat(r.amount || 0).toFixed(2),
      Date: r.transaction_date,
      "Payment Method": r.payment_method || "",
      "Reference Number": r.reference_number || "",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Jutem Expenses");
    XLSX.writeFile(
      wb,
      `Jutem_Expenses_${moment().format("YYYY-MM-DD")}.xlsx`
    );
  };

  const clearFilters = () => {
    setSearchText("");
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedCategory(null);
  };

  const fmtK = (v) =>
    `K ${(v || 0).toLocaleString("en-ZM", { minimumFractionDigits: 2 })}`;

  const columns = [
    {
      title: "#",
      key: "index",
      width: 50,
      render: (_, __, i) => i + 1,
    },
    {
      title: "Description",
      dataIndex: "notes",
      key: "notes",
      sorter: (a, b) => (a.notes || "").localeCompare(b.notes || ""),
      render: (v) => <Text strong>{v || "N/A"}</Text>,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      sorter: (a, b) => (a.amount || 0) - (b.amount || 0),
      render: (v) => (
        <Text style={{ color: zambianColors.red }}>{fmtK(v)}</Text>
      ),
      align: "right",
    },
    {
      title: "Date",
      dataIndex: "transaction_date",
      key: "transaction_date",
      sorter: (a, b) =>
        (a.transaction_date || "").localeCompare(b.transaction_date || ""),
      render: (v) => (v ? moment(v).format("DD MMM YYYY") : "-"),
    },
    {
      title: "Payment Method",
      dataIndex: "payment_method",
      key: "payment_method",
      render: (v) => {
        const colors = {
          cash: "green",
          bank_transfer: "blue",
          mobile_money: "orange",
        };
        return <Tag color={colors[v] || "default"}>{v || "N/A"}</Tag>;
      },
    },
    {
      title: "Reference",
      dataIndex: "reference_number",
      key: "reference_number",
      render: (v) => <Text type="secondary">{v || "-"}</Text>,
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 100 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">Loading Jutem Expenses...</Text>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "16px" }}>
      {/* Header */}
      <Row
        justify="space-between"
        align="middle"
        style={{ marginBottom: 16 }}
      >
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            Jutem Expenses
          </Title>
          <Text type="secondary">
            {filteredData.length} expense entries
            {selectedMonth
              ? ` for ${moment(selectedMonth, "YYYY-MM").format("MMMM YYYY")}`
              : selectedYear
              ? ` for ${selectedYear}`
              : ` across ${availableMonths.length} month(s)`}
          </Text>
        </Col>
        <Col>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate("/floatManagement")}
            >
              Add Expense
            </Button>
            <Button icon={<DownloadOutlined />} onClick={handleExport}>
              Export
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              borderRadius: 8,
              background: zambianColors.red,
              boxShadow: "0 2px 8px rgba(209, 107, 107, 0.2)",
            }}
          >
            <Statistic
              title={
                <span style={{ color: "#fff", fontSize: 14, fontWeight: 500 }}>
                  Total Expenses
                </span>
              }
              value={stats.totalExpenses}
              precision={2}
              prefix={<DollarOutlined style={{ color: "#fff", fontSize: 24 }} />}
              valueStyle={{ color: "#fff", fontSize: 32, fontWeight: "bold" }}
              suffix={<span style={{ color: "#fff", fontSize: 14 }}>ZMW</span>}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              borderRadius: 8,
              background: zambianColors.orange,
              boxShadow: "0 2px 8px rgba(233, 158, 84, 0.2)",
            }}
          >
            <Statistic
              title={
                <span style={{ color: "#fff", fontSize: 14, fontWeight: 500 }}>
                  Transactions
                </span>
              }
              value={stats.transactionCount}
              prefix={<FundOutlined style={{ color: "#fff", fontSize: 24 }} />}
              valueStyle={{ color: "#fff", fontSize: 32, fontWeight: "bold" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              borderRadius: 8,
              background: zambianColors.black,
              boxShadow: "0 2px 8px rgba(68, 68, 68, 0.2)",
            }}
          >
            <Statistic
              title={
                <span style={{ color: "#fff", fontSize: 14, fontWeight: 500 }}>
                  Highest Expense
                </span>
              }
              value={stats.highestExpense}
              precision={2}
              prefix={<RiseOutlined style={{ color: "#fff", fontSize: 24 }} />}
              valueStyle={{ color: "#fff", fontSize: 32, fontWeight: "bold" }}
              suffix={<span style={{ color: "#fff", fontSize: 14 }}>ZMW</span>}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              borderRadius: 8,
              background: zambianColors.green,
              boxShadow: "0 2px 8px rgba(77, 140, 74, 0.2)",
            }}
          >
            <Statistic
              title={
                <span style={{ color: "#fff", fontSize: 14, fontWeight: 500 }}>
                  Average Expense
                </span>
              }
              value={stats.averageExpense}
              precision={2}
              prefix={<BankOutlined style={{ color: "#fff", fontSize: 24 }} />}
              valueStyle={{ color: "#fff", fontSize: 32, fontWeight: "bold" }}
              suffix={<span style={{ color: "#fff", fontSize: 14 }}>ZMW</span>}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={8} md={6}>
            <Input
              placeholder="Search by description..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={12} sm={4} md={3}>
            <Select
              placeholder="Year"
              value={selectedYear}
              onChange={(val) => {
                setSelectedYear(val);
                setSelectedMonth(null);
              }}
              allowClear
              style={{ width: "100%" }}
            >
              {availableYears.map((y) => (
                <Select.Option key={y} value={y}>
                  {y}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={5} md={4}>
            <Select
              placeholder="Month"
              value={selectedMonth}
              onChange={setSelectedMonth}
              allowClear
              style={{ width: "100%" }}
            >
              {availableMonths.map((m) => (
                <Select.Option key={m} value={m}>
                  {moment(m, "YYYY-MM").format("MMM YYYY")}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={5} md={4}>
            <Select
              placeholder="Category"
              value={selectedCategory}
              onChange={setSelectedCategory}
              allowClear
              showSearch
              style={{ width: "100%" }}
            >
              {availableCategories.map((c) => (
                <Select.Option key={c} value={c}>
                  {c}
                </Select.Option>
              ))}
            </Select>
          </Col>
          {(searchText || selectedYear || selectedMonth || selectedCategory) && (
            <Col>
              <Button type="link" onClick={clearFilters} danger>
                Clear Filters
              </Button>
            </Col>
          )}
        </Row>
      </Card>

      {/* Data Table */}
      <Card title={`Jutem Expenses (${filteredData.length} entries)`}>
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (t) => `${t} entries`,
          }}
          scroll={{ x: true }}
          summary={() => {
            const total = filteredData.reduce(
              (s, r) => s + parseFloat(r.amount || 0),
              0
            );
            return (
              <Table.Summary fixed>
                <Table.Summary.Row style={{ backgroundColor: "#f5f5f5" }}>
                  <Table.Summary.Cell index={0} />
                  <Table.Summary.Cell index={1}>
                    <Text strong>TOTALS</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">
                    <Text strong style={{ color: zambianColors.red }}>
                      {fmtK(total)}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} />
                  <Table.Summary.Cell index={4} />
                  <Table.Summary.Cell index={5} />
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
          locale={{
            emptyText: (
              <Empty
                description="No expense data found."
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />
      </Card>
    </div>
  );
};

export default JutemExpensesPage;
